import React from 'react';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import { IconButton, Paper, Box, CircularProgress } from '@material-ui/core';
import Typography from '@material-ui/core/Typography';
import KeyboardArrowLeft from '@material-ui/icons/KeyboardArrowLeft';
import KeyboardArrowRight from '@material-ui/icons/KeyboardArrowRight';
import SwipeableViews from 'react-swipeable-views';
import PlayCircleOutlineIcon from '@material-ui/icons/PlayCircleOutline';
import YouTube from 'react-youtube';

const AutoPlaySwipeableViews = SwipeableViews;

const useStyles = makeStyles((theme) => ({
    root: {
        flexGrow: 1,
    },
    header: {
        display: 'flex',
        alignItems: 'center',
        height: 50,
        paddingLeft: theme.spacing(4),
        backgroundColor: theme.palette.background.default,
    },
    img: {
        display: 'block',
        maxWidth: '100%',
        overflow: 'hidden',
        width: '100%',
    },
    stepper: {
        position: 'relative',
        display: 'flex',
        padding: theme.spacing(1),
        backgroundColor: theme.palette.grayBackground.main,
        alignItems: 'center',
        '& *': {
            flexGrow: 0
        }
    },
    stepperInner: {
        overflow: 'hidden',
        position: 'relative',
        flexGrow: 1,
        height: 0,
        paddingBottom: '14%'
    },
    stepperSlider: {
        position: 'absolute',
        left: 0,
        top: 0,
        display: 'flex',
        flexWrap: 'nowrap',
        height: '100%',
        width: '100%',
        transition: 'left 250ms'
    },
    step: {
        minWidth: '20%',
        margin: '0 6px',
        backgroundPosition: 'center center',
        backgroundSize: 'contain',
        borderRadius: theme.spacing(1),
        position: 'relative',
        border: `solid 2px ${theme.palette.grayBackground.main}`,
        backgroundColor: theme.palette.grey.medium,
        backgroundRepeat: 'no-repeat',
    },
    stepActive: {
        borderColor: theme.palette.secondary.main
    },
    carouselMainImage: {
        backgroundSize: 'contain',
        backgroundPosition: 'center center',
        backgroundRepeat: 'no-repeat',
        backgroundColor: theme.palette.grey.medium,
        width: '100%',
        height: 0,
        paddingBottom: '60%',
        border: `solid 1px ${theme.palette.grey.medium}`
    },
    playButton: {
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        '& svg': {
            fontSize: '3rem',
            color: 'white'
        }
    },
    youtubeVideoPlayer: {
        width: '100%',
        height: '100%',
        position: 'absolute',
        top: 0,
        left: 0,
        visibility: 'hidden'
    },
    youtubeVideoPlayerPlaying: {
        visibility: 'visible'
    },
    stepPlayIcon: {
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        color: 'white'
    }
}));

function Carousel({steps}) {
    const classes = useStyles();
    const theme = useTheme();
    const [activeStep, setActiveStep] = React.useState(0);
    const [playable, setPlayable] = React.useState(null);
    const [playing, setPlaying] = React.useState(false);
    const maxSteps = steps.length;
    const stepperInnerRef = React.useRef();
    const stepperSliderRef = React.useRef();

    const handleNext = () => {
        setActiveStep((prevActiveStep) => prevActiveStep + 1);
    };

    const handleBack = () => {
        setActiveStep((prevActiveStep) => prevActiveStep - 1);
    };

    const handleStepChange = (step) => {
        setActiveStep(step);
    };

    const handlePlayVideo = (index) => {
        setPlayable(index);
    };

    const handleOnReady = (e) => {
        setPlaying(true);
        e.target.playVideo();
    }

    React.useEffect(() => {
        const step = stepperSliderRef.current.childNodes[activeStep];
        const stepLeft = step.offsetLeft;
        const stepWidth = step.offsetWidth;
        const stepRight = stepLeft + stepWidth;
        const stepperWidth = stepperInnerRef.current.offsetWidth;
        const sliderLeft = parseInt(stepperSliderRef.current.style.left.replace('px', '')) || 0;
        let slide = false, newLeft = 0;

        setPlayable(null);
        setPlaying(false);

        if (stepRight >= stepperWidth - sliderLeft) {
            slide = true;
            newLeft = stepLeft + stepWidth/2 - stepperWidth / 2;
        }

        if (stepLeft <= Math.abs(sliderLeft)) {
            slide = true;
            newLeft = stepLeft + stepWidth/2 - stepperWidth / 2;
        }

        if (slide) {
            stepperSliderRef.current.style.left = `${-1 * newLeft}px`;
        }
    }, [activeStep]);

    return (
        <div className={classes.root}>
            {steps[activeStep].label && (
                <Paper square elevation={0} className={classes.header}>
                    <Typography>{steps[activeStep].label}</Typography>
                </Paper>
            )}
            <AutoPlaySwipeableViews
                axis={theme.direction === 'rtl' ? 'x-reverse' : 'x'}
                index={activeStep}
                onChangeIndex={handleStepChange}
                enableMouseEvents
            >
                {steps.map((step, index) => (
                    <div key={index}>
                        {Math.abs(activeStep - index) <= 2 ? (
                            <div className={classes.carouselMainImage} style={{backgroundImage: `url(${step.imgPath})`}}>
                                {step.type === 'video' && !playing && (
                                    <IconButton className={classes.playButton} onClick={() => {handlePlayVideo(index)}}>
                                        {playable === null && <PlayCircleOutlineIcon />}
                                        {playable === index && <CircularProgress />}
                                    </IconButton>
                                )}
                                {playable === index && (
                                    <YouTube
                                        className={`${classes.youtubeVideoPlayer} ${playing ? classes.youtubeVideoPlayerPlaying : ''}`}
                                        videoId="XzoxaKuWd50" opts={{
                                            playerVars: {
                                                autoplay: 1,
                                                rel: 0
                                            }
                                        }}
                                        onReady={handleOnReady}
                                    />
                                )}
                            </div>
                        ) : null}
                    </div>
                ))}
            </AutoPlaySwipeableViews>

            <Box className={classes.stepper}>

                <div>
                    <IconButton color="primary" onClick={handleBack} disabled={activeStep === 0}>
                        {theme.direction === 'rtl' ? <KeyboardArrowRight /> : <KeyboardArrowLeft />}
                    </IconButton>
                </div>
                <Box ref={stepperInnerRef} className={classes.stepperInner}>
                    <Box ref={stepperSliderRef} className={classes.stepperSlider}>
                        {steps.map((step, index) => (
                            <Box style={{backgroundImage: `url(${step.imgPath})`}} key={index} onClick={() => {handleStepChange(index)}} className={`${classes.step} ${activeStep === index ? classes.stepActive : ''}`}>
                                {step.type === 'video' && <PlayCircleOutlineIcon className={classes.stepPlayIcon} />}
                            </Box>
                        ))}
                    </Box>
                </Box>
                <div>
                    <IconButton color="primary" onClick={handleNext} disabled={activeStep === maxSteps - 1}>
                        {theme.direction === 'rtl' ? <KeyboardArrowLeft /> : <KeyboardArrowRight />}
                    </IconButton>
                </div>
            </Box>
        </div>
    );
}

export default Carousel;
